import { DASHBOARD_TYPE } from '@const/dashboard.constants';
import { BOX_TYPE } from "@const/pageResponse.constants";
import { BasicResponseDTO } from './basicResponse.dto';
import { ItemGroup, ItemGroupExcludeWithLabels } from "./customGroup.dto";
import { MenuItem } from './menuResponse.dto';
import { CapacityTableBox, ChartType, GraphBase, GraphInterval, MetricAggregate } from './pageResponse.dto';

export type DashboardType = typeof DASHBOARD_TYPE[keyof typeof DASHBOARD_TYPE];

export class DashboardDTO {
  dashboard_id: string;
  label: string;
  type: DashboardType;
  groups: DashboardGroupDTO[];
  folder: string;
  default: boolean;
  width: number;
}

export class DefaultDashboard {
  dashboard: DashboardDTO;
  path: MenuItem[];
}

export class DefaultDashboardDTO extends BasicResponseDTO {
  data: DefaultDashboard;
}

export class DashboardGroupDTO {
  dashboard_group_id: string;
  dashboard_id?: string;
  label: string;
}

export class DashboardGroupsDTO extends BasicResponseDTO {
  data: DashboardGroupDTO[];
}

export class DashboardTableDataRequest {
  tab?: string;
  subtab?: string;
  tabId?: string;
  url?: string;
  id?: string;
}

export class DashboardTable extends DashboardTableDataRequest implements DashboardItem {
  width: number;
  height: number;
  x: number;
  y: number;
  dashboard_item_id: string;
  label?: string;
  type: 'table';
}

export interface DashboardItem {
  dashboard_item_id: string;
  label?: string;
  width: number;
  height: number;
  x: number;
  y: number;
  type: typeof BOX_TYPE.FourGraphBox | 'graph' | 'custom_graph' | 'custom_graph_per_item' | 'table';

  tab?: string; // graph related
  subtab?: string; // graph related
  url?: string; // graph related
  interval?: GraphInterval; // graph related
}

export class DashboardItem {
  static equals(a: DashboardItem, b: DashboardItem) {
    return a.tab === b.tab && a.interval === b.interval && a.url === b.url;
  }
}


export class DashboardGraph extends GraphBase implements DashboardItem {
  // so it is placed to end before compact
  static readonly DEFAULT_COORDINATE = 99;
  dashboard_item_id: string;
  label: string;
  width: number;
  height: number;
  x: number;
  y: number;
  type: typeof BOX_TYPE.FourGraphBox | 'graph' | 'custom_graph' | 'custom_graph_per_item';

  url?: string; // graph related
  interval?: GraphInterval; // graph related
  tab?: string; // graph related
  subtab?: string; // graph related
  tabId?: string; // graph related
  id?: string; // graph related

  labels?: string[];  // graph per item related
  options?: { // graph per item related
    per_item_width?: number;
    per_item_height?: number;
  };

  static isMultiGraph(di: DashboardItem): di is DashboardGraph {
    return di.type === 'custom_graph_per_item';
  }
}

export class DashboardGroup {
  graphs: DashboardGraph[];
  tables: DashboardTable[];
  label: string;
  width: number;
  height: number;
  x: number;
  y: number;
  dashboard_group_id: string;
}

export class DashboardFolder {
  dashboard_folder_id: string;
  label: string;
  type: DashboardType;
}

export class CreateDashboardItemDTO {
  dashboard_group_id: string;
  url: string;
  tab: string;
  subtab?: string;
  tabId?: string;
  id?: string;
  interval?: GraphInterval;
  type: DashboardItemType;
  options?: { // graph per item related
    per_item_width?: number;
    per_item_height?: number;
  };
}

export class CreateDashboardCustomGraphDTO extends ItemGroup {
  dashboard_group_id: string;
  label: string;
  class?: string; // item group related
  hw_type?: string; // item group related
  subsystem: string; // item group related
  metrics: CustomGraphMetric[];
  per_item?: boolean;
  interval?: GraphInterval;
}

export class CustomGraphMetric {
  metric: string;
  type: ChartType;
  stacked: boolean;
  order: number;
  aggregation?: MetricAggregate;
  color?: string;
}

export class DashboardCustomGraphPreview {
  dashboard_custom_graph_id: string;
  dashboard_group_id: string;
  label: string;

  class?: string; // item group related
  hw_type?: string; // item group related
  subsystem: string; // item group related

  interval?: GraphInterval; // graph related
  path?: string; // dashbord path, only for preview
  dashboard_group_label?: string; // only for preview
}

export class DashboardCustomGraphPreviewDTO extends BasicResponseDTO {
  data: DashboardCustomGraphPreview[];
}

export class DashboardCustomGraph extends ItemGroupExcludeWithLabels {
  dashboard_custom_graph_id: string;
  dashboard_group_id: string;
  item_group_id?: string;
  label: string;
  class?: string; // item group related
  hw_type?: string; // item group related
  subsystem: string; // item group related
  metrics: CustomGraphMetric[];
  per_item?: boolean;
  interval?: GraphInterval;
}

export class DashboardCustomGraphsResponseDTO extends BasicResponseDTO {
  data: DashboardCustomGraph[];
}

export class DashboardCustomGraphResponseDTO extends BasicResponseDTO {
  data: DashboardCustomGraph;
}

type DashboardItemType = 'graph' | 'table' | 'custom_graph';

export class CreateGrafanaGraphDTO {
  grafana_dashboard_uid: string;
  url: string;
  tab: string;
  subtab?: string;
  tabId?: string;
  id?: string;
  interval: GraphInterval;
}

export class CreateGrafanaTableDTO {
  grafana_dashboard_uid: string;
  url: string;
  tab: string;
  subtab?: string;
  tabId: string;
  id?: string;
}

export class GrafanaTreeItem {
  type: 'folder' | 'dashboard';
  uid: number;
  label: string;
  children?: GrafanaTreeItem[];
}


export class FindDashboardItemDTO {
  url: string;
  tab: string;
  subtab?: string;
  interval?: GraphInterval;
  tabId?: string;
  id?: string;
  type?: DashboardItemType;
}

export class CreateDTO {
  label: string;
  parent: string;
  group_id?: number;
}

export class UpdateDashboardDTO {
  label?: string;
  parent?: string;
  group_id?: number;
  width?: number;
}

export class UpdateDashboardFolderDTO {
  label?: string;
  parent?: string;
  group_id?: number;
}

export class CreateResponseDTO extends BasicResponseDTO {
  data: {
    id: string;
    label?: string;
    parent?: string;
  };
}

export class FindItem {
  interval?: GraphInterval;
  dashboard_item_id: string;
  dashboard_group: {
    label: string;
    dashboard_group_id: string;
  };
  dashboard: {
    label: string;
    dashboard_id: string;
  };
  group_id?: number;
  type?: string;
  path?: string;
}

export class FindItemsDTO extends BasicResponseDTO {
  data: FindItem[];
}

export class FindGrafanaItem {
  dashboard: {
    grafana_dashboard_uid: string;
    label: string;
  };
  grafana_item_uid: string;
  interval?: GraphInterval;
  id?: string;
}

export class FindGrafanaItemsDTO extends BasicResponseDTO {
  data: FindGrafanaItem[];
}

export class UpdateDashboardItemDTO {
  width?: number;
  height?: number;
  url?: string;
  x?: number;
  y?: number;
  interval?: GraphInterval;
  tab?: string;
  subtab?: string;
  tabId?: string;
  dashboard_group_id: string;
  options?: { // graph per item related
    per_item_width?: number;
    per_item_height?: number;
  };

  constructor(item?: DashboardItem) {
    if (!item) return;

    this.width = item.width;
    this.height = item.height;
    this.url = item.url;
    this.x = item.x;
    this.y = item.y;
    this.interval = item.interval;
    if (DashboardGraph.isMultiGraph(item)) {
      this.options = item.options;
    }
  }
}

export class UpdateDashboardGroupDTO {
  width: number;
  height: number;
  x: number;
  y: number;
  label: string;
  dashboard_id: string;

  constructor(group?: DashboardGroup) {
    if (!group) return;

    this.width = group.width;
    this.height = group.height;
    this.x = group.x;
    this.y = group.y;
    this.label = group.label;
  }
}

export class GroupDashboardFolder extends MenuItem {
  group_id: number;
}

export class GroupDashboardFoldersDTO extends BasicResponseDTO {
  data: GroupDashboardFolder[];
}

export class GrafanaTreeDTO extends BasicResponseDTO {
  data: GrafanaTreeItem[];
}

export class DashboardTableData extends BasicResponseDTO {
  data: CapacityTableBox;
}

export class DashboardTreeItem {
  type: string; // dashboard, folder, group
  id: string;
  label: string;
  children?: DashboardTreeItem[];
}

export class DashboardTreeItemResponseDTO extends BasicResponseDTO {
  data: DashboardTreeItem[];
}